import { FieldValueLookupError } from '@atlassian/jira-issue-adjustments/src/common/errors/field-value-lookup-error.tsx';
import type { OptionsVisibility } from '@atlassian/jira-issue-adjustments/src/common/types/options-visibility.tsx';
import type { MultiCheckboxSetPublicValue } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/fields/multi-checkbox.tsx';
import { applyOptionsVisibilityRule } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/utils/apply-options-visibility-rule/index.tsx';
import { getAvailableOptionsVisibilityRule } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/utils/get-available-options-visibility-rule/index.tsx';
import { getValueLookupValuesDiff } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/utils/get-value-lookup-values-diff/index.tsx';
import { isNotEmptyString } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/utils/is-not-empty-string/index.tsx';
import { multiValueLookup } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/utils/multi-value-lookup/index.tsx';

export type MultiCheckboxGetPublicValue = {
	id: string;
	value: string;
};

export type MultiCheckboxInternalAllowedValue = {
	id: string;
	value: string;
};

export type MultiCheckboxInternalValue = {
	label: string;
	value: string;
};

export const transformMultiCheckboxSetOptionsVisibility = (
	optionsVisibility: OptionsVisibility,
	lookupValues: MultiCheckboxInternalAllowedValue[],
): MultiCheckboxInternalAllowedValue[] =>
	applyOptionsVisibilityRule(optionsVisibility, lookupValues, 'id');

export const transformMultiCheckboxGetOptionsVisibility = (
	optionsVisibility: OptionsVisibility,
	lookupValues: MultiCheckboxInternalAllowedValue[],
): OptionsVisibility => getAvailableOptionsVisibilityRule(optionsVisibility, lookupValues, 'id');

export const validateMultiCheckboxSetValue = (
	value: unknown,
): value is MultiCheckboxSetPublicValue => Array.isArray(value) && value.every(isNotEmptyString);

export const multiCheckboxSetValueTransformer = (
	publicValue: MultiCheckboxSetPublicValue,
	allowedValues?: MultiCheckboxInternalAllowedValue[],
): MultiCheckboxInternalValue[] => {
	if (!publicValue.length) return [];

	const foundValue = multiValueLookup<MultiCheckboxGetPublicValue>(
		publicValue,
		allowedValues,
		'id',
	);
	if (foundValue.length !== publicValue.length) {
		const notFoundValues = getValueLookupValuesDiff(publicValue, foundValue, 'id');

		throw new FieldValueLookupError(notFoundValues.join());
	}

	return foundValue.map(({ value, id }) => ({ label: value, value: id }));
};

export const multiCheckboxGetValueTransformer = (
	internalValue: MultiCheckboxInternalValue[],
): MultiCheckboxGetPublicValue[] => {
	if (!internalValue) return [];

	return internalValue.map(({ value, label }) => ({
		id: value,
		value: label,
	}));
};
